import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, PauseCircle, X, Hotel, IndianRupee, Paperclip, ShieldAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

/**
 * Admin1PricingModal — Phase 7, Hotel/Travel v7 `admin1_review` (CAO).
 * Approve → Approx Price OPTIONAL + Documents attachment (kind=admin1_doc) OPTIONAL.
 *           Remarks OPTIONAL.
 * Hold/Reject → Remarks MANDATORY (min 2 chars); price/attachment ignored.
 */
const PRICE_RE = /^[A-Za-z0-9 ,.\-₹$/()+]+$/;

export default function Admin1PricingModal({ open, onClose, onDone, requisition, action, actingAsBanner = null }) {
  const [remarks, setRemarks] = useState("");
  const [price, setPrice] = useState("");
  const [docFile, setDocFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const textareaRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setRemarks(""); setPrice(""); setDocFile(null); setError("");
    setTimeout(() => textareaRef.current?.focus(), 20);
  }, [open, action, requisition?.ref_code]);

  if (!open || !requisition) return null;

  const cfg = {
    approve: { label: "Approve", pastTense: "Approved", Icon: CheckCircle2, tone: "teal",
               hint: "Add Approx Price and any relevant documents (bookings, quotes, correspondence)." },
    on_hold: { label: "Place on Hold", pastTense: "On-hold", Icon: PauseCircle, tone: "gold" },
    reject:  { label: "Reject", pastTense: "Rejected", Icon: XCircle, tone: "danger" },
  }[action];
  if (!cfg) return null;

  const trimmedRemarks = remarks.trim();
  const trimmedPrice = price.trim();
  const remarksMandatory = action !== "approve";
  const remarksOk = true; // Phase 12.1 — no length rule
  const priceOk = !trimmedPrice || PRICE_RE.test(trimmedPrice);
  const canSubmit = remarksOk && priceOk && !busy;

  const btnCls = {
    teal:   "bg-[#2d6b6b] hover:bg-[#1c4a4a] border-[#1c4a4a]",
    gold:   "bg-[#b48a45] hover:bg-[#8a6a2f] border-[#8a6a2f]",
    danger: "bg-[#b3261e] hover:bg-[#8a1c17] border-[#8a1c17]",
  }[cfg.tone];

  const uploadFile = async () => {
    if (!docFile) return null;
    const fd = new FormData();
    fd.append("file", docFile);
    const { data } = await api.post("/upload", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return {
      field_name: "admin1_doc",
      filename: data.filename, url: data.url, size: data.size, kind: "admin1_doc",
    };
  };

  const submit = async () => {
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      const payload = { action, remarks: trimmedRemarks };
      if (action === "approve") {
        if (trimmedPrice) payload.admin1_approx_price = trimmedPrice;
        try {
          const att = await uploadFile();
          if (att) payload.attachments = [att];
        } catch {
          toast.warning("Attachment upload failed — approving without it.");
        }
      }
      const { data } = await api.post(`/requisitions/${requisition.ref_code}/action`, payload);
      toast.success(`${cfg.pastTense} — ${requisition.ref_code}`);
      onDone?.(data); onClose?.();
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg, { duration: 5000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
         onClick={onClose} data-testid="admin1-pricing-modal">
      <div className="w-full max-w-lg bg-[#fffdf9] rounded-xl shadow-2xl border border-[#e2d7c3] overflow-hidden"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#e2d7c3] bg-white">
          <div className="flex items-center gap-2">
            <Hotel className="text-[#2d6b6b]" size={18} />
            <h3 className="font-serif text-lg font-semibold text-[#1f2320]">
              Admin-1 · {cfg.label} — {requisition.ref_code}
            </h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-[#f4e9eb]" aria-label="Close">
            <X size={18} className="text-[#4a524c]" />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          {actingAsBanner && (
            <div className="text-xs text-[#7a1f2b] bg-[#faf5eb] border border-[#b48a45] rounded px-3 py-2 flex items-start gap-2">
              <ShieldAlert size={14} className="mt-0.5 shrink-0 text-[#b48a45]" />
              <div>Acting as <strong>{actingAsBanner}</strong>.</div>
            </div>
          )}
          {cfg.hint && (
            <div className="text-xs text-[#4a524c] bg-[#faf5eb] border-l-4 border-[#2d6b6b] px-3 py-2 rounded">
              <strong>{cfg.hint}</strong>
            </div>
          )}
          {error && (
            <div className="text-xs text-white bg-[#b3261e] px-3 py-2 rounded">{error}</div>
          )}

          {action === "approve" && (
            <>
              <label className="block">
                <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                  Approx Price
                  <span className="ml-2 text-[10px] uppercase tracking-widest text-[#6e7770]">optional · alphanumeric</span>
                </span>
                <div className="relative">
                  <IndianRupee size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6e7770]" />
                  <input type="text" value={price} onChange={(e) => setPrice(e.target.value.slice(0, 120))}
                         placeholder="e.g. ₹ 45,000"
                         className={`input-vgws pl-9 ${trimmedPrice && !priceOk ? "!border-[#b3261e]" : ""}`}
                         data-testid="admin1-approx-price" />
                </div>
              </label>
              <label className="block">
                <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
                  Documents <span className="text-[#6e7770] text-xs font-normal">(optional)</span>
                </span>
                <div className="flex items-center gap-2">
                  <input type="file" onChange={(e) => setDocFile(e.target.files?.[0] || null)}
                         accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
                         className="text-xs text-[#4a524c]"
                         data-testid="admin1-doc" />
                  {docFile && (
                    <span className="text-xs text-[#2d6b6b] inline-flex items-center gap-1">
                      <Paperclip size={12} /> {docFile.name}
                    </span>
                  )}
                </div>
              </label>
            </>
          )}

          <label className="block">
            <span className="block text-sm font-medium text-[#1f2320] mb-1.5">
              Remarks {remarksMandatory
                ? <span className="text-[#b3261e]">*</span>
                : <span className="text-[#6e7770] text-xs font-normal">(optional)</span>}
            </span>
            <textarea ref={textareaRef} value={remarks} onChange={(e) => setRemarks(e.target.value.slice(0, 1000))}
                      rows={4}
                      className={`input-vgws min-h-[100px] ${remarksMandatory && trimmedRemarks && !remarksOk ? "!border-[#b3261e]" : ""}`}
                      data-testid="admin1-remarks" />
          </label>
        </div>
        <div className="px-5 py-4 border-t border-[#e2d7c3] flex items-center justify-end gap-2 bg-[#faf5eb]">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={submit} disabled={!canSubmit}
                  className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white border shadow-sm ${btnCls} ${!canSubmit ? "opacity-50 cursor-not-allowed" : ""}`}
                  data-testid="admin1-pricing-confirm">
            {busy ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <cfg.Icon size={16} />}
            {busy ? "Please wait…" : `Confirm ${cfg.label}`}
          </button>
        </div>
      </div>
    </div>
  );
}
